import React, { useState, useEffect, useCallback } from 'react';
import { scheduledCallsAPI } from '../api/api';
import {
  Phone, PhoneCall, Clock, CheckCircle, X, Plus, CalendarBlank,
  SpinnerGap, CaretDown, CaretUp, ArrowsClockwise, XCircle, Check
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

export const ScheduleCallButton = ({ partnerId, partnerName, projectId, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [slots, setSlots] = useState([{ date: '', time: '' }]);
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async () => {
    const validSlots = slots.filter(s => s.date && s.time);
    if (validSlots.length === 0) { toast({ title: 'Select at least one time', variant: 'destructive' }); return; }
    setSending(true);
    try {
      const res = await scheduledCallsAPI.create({ partner_id: partnerId, proposed_slots: validSlots, reason, project_id: projectId || null });
      if (res.data?.success) { toast({ title: 'Call scheduled!', description: `Waiting for ${partnerName} to confirm.` }); setOpen(false); setSlots([{ date: '', time: '' }]); setReason(''); }
    } catch (err) { toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to schedule', variant: 'destructive' }); }
    finally { setSending(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className={`flex items-center gap-1.5 ${className}`} data-testid={`schedule-call-btn-${partnerId}`}>
        <PhoneCall size={16} weight="bold" /> Schedule Call
      </button>
    );
  }

  return (
    <div className="bg-violet-50 dark:bg-violet-900/20 border-2 border-violet-300 dark:border-violet-700 rounded-lg p-4 mt-2" data-testid="schedule-call-form">
      <p className="text-sm font-bold text-violet-800 dark:text-violet-300 mb-3 flex items-center gap-2">
        <PhoneCall size={16} weight="bold" /> Schedule a call with {partnerName}
      </p>
      <div className="space-y-2 mb-3">
        {slots.map((slot, idx) => (
          <div key={idx} className="flex gap-2">
            <input type="date" value={slot.date} onChange={(e) => { const s = [...slots]; s[idx].date = e.target.value; setSlots(s); }}
              className="flex-1 h-9 px-2 text-xs bg-white dark:bg-neutral-800 border-2 border-violet-300 dark:border-violet-600 rounded-lg text-neutral-900 dark:text-white" data-testid={`call-date-${idx}`} />
            <select value={slot.time} onChange={(e) => { const s = [...slots]; s[idx].time = e.target.value; setSlots(s); }}
              className="w-36 h-9 px-2 text-xs bg-white dark:bg-neutral-800 border-2 border-violet-300 dark:border-violet-600 rounded-lg text-neutral-900 dark:text-white" data-testid={`call-time-${idx}`}>
              <option value="">Select time</option>
              {['8:00 AM','9:00 AM','10:00 AM','11:00 AM','12:00 PM','1:00 PM','2:00 PM','3:00 PM','4:00 PM','5:00 PM','6:00 PM','7:00 PM'].map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {slots.length > 1 && <button onClick={() => setSlots(slots.filter((_, i) => i !== idx))} className="h-9 w-9 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-lg border border-red-300 flex items-center justify-center"><X size={12} weight="bold" /></button>}
          </div>
        ))}
        {slots.length < 3 && <button onClick={() => setSlots([...slots, { date: '', time: '' }])} className="text-xs text-violet-600 dark:text-violet-400 font-bold hover:underline flex items-center gap-1"><Plus size={12} weight="bold" /> Add another time</button>}
      </div>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What would you like to discuss? (optional)"
        className="w-full h-9 px-2 text-xs bg-white dark:bg-neutral-800 border-2 border-violet-300 dark:border-violet-600 rounded-lg text-neutral-900 dark:text-white mb-3" data-testid="call-reason-input" />
      <div className="flex gap-2">
        <button onClick={handleSubmit} disabled={sending || !slots.some(s => s.date && s.time)}
          className="flex-1 h-9 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold rounded-lg border-2 border-black disabled:opacity-40 flex items-center justify-center gap-1 transition-all" data-testid="send-call-request">
          {sending ? <SpinnerGap size={14} weight="bold" className="animate-spin" /> : <PhoneCall size={14} weight="bold" />} Send Request
        </button>
        <button onClick={() => setOpen(false)} className="h-9 px-4 bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 text-xs font-bold rounded-lg border-2 border-neutral-400 dark:border-neutral-600">Cancel</button>
      </div>
    </div>
  );
};

export const ScheduledCallsList = ({ compact = false }) => {
  const [calls, setCalls] = useState([]);
  const [pending, setPending] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const fetchCalls = useCallback(async () => {
    try {
      const res = await scheduledCallsAPI.list();
      if (res.data?.success) {
        setCalls(res.data.calls || []);
        setPending(res.data.pending || []);
        setUpcoming(res.data.upcoming || []);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  const handleConfirm = async (callId, slotIndex) => {
    try {
      const res = await scheduledCallsAPI.confirm(callId, slotIndex);
      if (res.data?.success) { toast({ title: 'Call confirmed!' }); fetchCalls(); }
    } catch (err) { toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  const handleComplete = async (callId) => {
    try {
      await scheduledCallsAPI.complete(callId);
      toast({ title: 'Call marked as completed' });
      fetchCalls();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleCancel = async (callId) => {
    try {
      await scheduledCallsAPI.cancel(callId);
      toast({ title: 'Call cancelled' });
      fetchCalls();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  if (loading) return <div className="flex justify-center py-4"><SpinnerGap size={24} weight="bold" className="animate-spin text-[#2B4C6F]" /></div>;

  const activeCalls = [...upcoming, ...pending];
  if (activeCalls.length === 0 && compact) return null;

  const statusConfig = {
    pending: { label: 'Awaiting Confirmation', cls: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700' },
    confirmed: { label: 'Confirmed', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700' },
    completed: { label: 'Completed', cls: 'bg-neutral-100 text-neutral-500 border-neutral-300 dark:bg-neutral-800 dark:text-neutral-400 dark:border-neutral-600' },
    cancelled: { label: 'Cancelled', cls: 'bg-red-100 text-red-600 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700' },
  };

  return (
    <div className={compact ? '' : 'bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] mb-6 overflow-hidden'} data-testid="scheduled-calls-section">
      {!compact && (
        <div className="p-4 bg-violet-50 dark:bg-violet-900/20 border-b-2 border-black dark:border-neutral-700 flex items-center gap-3">
          <div className="w-9 h-9 bg-violet-600 rounded-lg flex items-center justify-center border-2 border-black">
            <PhoneCall size={18} weight="bold" className="text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-neutral-900 dark:text-white">Scheduled Calls</h2>
            <p className="text-xs text-neutral-500">{upcoming.length} upcoming, {pending.length} pending</p>
          </div>
        </div>
      )}

      {activeCalls.length === 0 ? (
        <div className="p-6 text-center">
          <Phone size={32} weight="bold" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-2" />
          <p className="text-sm text-neutral-500">No scheduled calls</p>
        </div>
      ) : (
        <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
          {activeCalls.map((call) => {
            const sc = statusConfig[call.status] || statusConfig.pending;
            const isExpanded = expanded === call.id;
            const needsConfirmation = call.status === 'pending' && !call.is_requester;

            return (
              <div key={call.id} data-testid={`call-card-${call.id}`}>
                <button onClick={() => setExpanded(isExpanded ? null : call.id)} className="w-full p-3 flex items-center gap-3 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors text-left">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center border-2 border-black flex-shrink-0 ${call.status === 'confirmed' ? 'bg-emerald-500' : 'bg-violet-500'}`}>
                    {call.status === 'confirmed' ? <CheckCircle size={20} weight="bold" className="text-white" /> : <PhoneCall size={20} weight="bold" className="text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-neutral-900 dark:text-white text-sm truncate">{call.partner_name}</p>
                    <div className="flex items-center gap-2 text-xs text-neutral-500 mt-0.5">
                      {call.selected_slot ? (
                        <span className="flex items-center gap-1 text-emerald-600 font-bold"><CalendarBlank size={12} weight="bold" /> {call.selected_slot.date} at {call.selected_slot.time}</span>
                      ) : (
                        <span>{call.proposed_slots?.length} time option{call.proposed_slots?.length !== 1 ? 's' : ''} proposed</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {needsConfirmation && <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />}
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded border ${sc.cls}`}>{sc.label}</span>
                    {isExpanded ? <CaretUp size={14} weight="bold" className="text-neutral-400" /> : <CaretDown size={14} weight="bold" className="text-neutral-400" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-dashed border-neutral-200 dark:border-neutral-700 pt-3">
                    {call.reason && <p className="text-xs text-neutral-600 dark:text-neutral-400 mb-3 italic">"{call.reason}"</p>}

                    {/* Proposed slots */}
                    {call.status === 'pending' && (
                      <div className="space-y-1.5 mb-3">
                        <p className="text-[10px] font-bold text-neutral-500 uppercase">Proposed Times</p>
                        {call.proposed_slots?.map((slot, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-neutral-50 dark:bg-neutral-900/50 rounded-lg px-3 py-2">
                            <span className="text-xs font-bold text-neutral-700 dark:text-neutral-300 flex items-center gap-1.5">
                              <CalendarBlank size={14} weight="bold" className="text-violet-500" /> {slot.date} at {slot.time}
                            </span>
                            {needsConfirmation && (
                              <button onClick={() => handleConfirm(call.id, idx)}
                                className="h-7 px-3 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold rounded border border-black flex items-center gap-1 transition-colors"
                                data-testid={`confirm-slot-${call.id}-${idx}`}>
                                <Check size={10} weight="bold" /> Confirm
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Confirmed slot */}
                    {call.status === 'confirmed' && call.selected_slot && (
                      <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 mb-3">
                        <p className="text-xs font-bold text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                          <CheckCircle size={14} weight="bold" /> Confirmed: {call.selected_slot.date} at {call.selected_slot.time}
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2">
                      {call.status === 'confirmed' && (
                        <button onClick={() => handleComplete(call.id)}
                          className="flex-1 h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg border-2 border-black flex items-center justify-center gap-1 transition-all"
                          data-testid={`complete-call-${call.id}`}>
                          <CheckCircle size={14} weight="bold" /> Mark Completed
                        </button>
                      )}
                      <button onClick={() => handleCancel(call.id)}
                        className="h-8 px-3 bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 text-xs font-bold rounded-lg border border-neutral-400 dark:border-neutral-600 flex items-center justify-center gap-1 transition-colors"
                        data-testid={`cancel-call-${call.id}`}>
                        <XCircle size={14} weight="bold" /> Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
